import momenttz from 'moment-timezone';
import moment from 'moment';
import { sortBy, last, filter, map, truncate } from 'lodash';
import { TIMEZONES, UNLIMITED_DATE } from '../constants/admin/settings';
import { toSelectionFormat } from './collection.utils';

// PUBLIC
export function getDayIntervals(startDate, endDate, intervals = []) {
    if (!startDate || !endDate) {
        return [];
    }
    const current = moment.utc(startDate);
    intervals.push(_momentDateToJavascript(current));
    if (current.isSameOrAfter(moment.utc(endDate))) {
        return intervals;
    } else {
        return getDayIntervals(current.add(1, 'days').valueOf(), endDate, intervals);
    }
}

export function msToHour(ms) {
    if (ms) {
        const time = moment.utc(ms);
        return _momentHourToString(time);
    } else {
        return '';
    }
}

export function msToLongTime(ms) {
    if (ms) {
        const time = moment(ms);
        return _momentDateToLongString(time);
    } else {
        return '';
    }
}

export function msToTime(ms, hour = false) {
    if (ms) {
        const time = moment(ms);
        return hour ? _momentDateHourToDisplayString(time) : _momentDateToDisplayString(time);
    } else {
        return '';
    }
}

export function msToDbTime(ms, hour = false) {
    if (ms) {
        const time = moment.utc(ms);
        return hour ? _momentDateHourToDisplayString(time) : _momentDateToString(time);
    } else {
        return '';
    }
}

export function timeToMs(time, add = 0) {
    return moment.utc(time).valueOf() + add;
}

export function toDisplayString(date = '', hour) {
    const time = moment(hour ? `${date} ${hour}` : (date && date.split ? date.split('T')[0] : date) || Date.now());
    return hour ? _momentDateHourToDisplayString(time) : _momentDateToDisplayString(time);
}

export function getHourIntervals(start, end, intervals = []) {
    const current = moment.utc(start);
    const endMoment = moment.utc(end);
    intervals.push(`${current.hour()}:00`);
    intervals.push(`${current.hour()}:30`);
    if (current.isSameOrAfter(endMoment)) {
        return intervals;
    } else {
        return getHourIntervals(_momentDateHourToString(current.add(1, 'h')), end, intervals);
    }
}
export function getMin(dates) {
    return sortBy(dates)[0];
}
export function getMax(dates) {
    return last(sortBy(dates));
}
export function oneYearFromNowInMs() {
    return moment().add('1', 'year').valueOf();
}
export function now() {
    const now = moment();
    return {
        day: _momentDateToString(now),
        hour: _momentHourToString(now)
    };
}

export function isBefore(day1, day2) {
    const moment1 = moment.utc(day1);
    const moment2 = moment.utc(day2);
    return moment1.isBefore(moment2);
}

export function minutesElapsed(start, end, current) {
    const momentStart = moment(start);
    const momentEnd = moment(end);
    const momentCurrent = moment.utc(`${current.day}T${current.hour}`);
    if (momentCurrent.isAfter(momentStart) && momentCurrent.isBefore(momentEnd)) {
        const startInMs = momentStart.valueOf();
        const currentInMs = momentCurrent.valueOf();
        const totalDone = currentInMs - startInMs;
        return totalDone / 1000 / 60;
    } else {
        return -100;
    }
}

export function startOfHour(dateHour) {
    return moment.utc(dateHour).startOf('hour').toISOString();
}

export function differenceInMinutes(fromTime, toTime) {
    return moment.duration(moment(toTime).utc().diff(moment(fromTime).utc())).as('minutes');
}

export function dateToWeekday(date) {
    return moment(date).format('dd');
}

export function dateToWeekdayShort(date) {
    return truncate(moment(date).format('dddd'), { omission: '', length: 3 }).toUpperCase();
}

export function weekDayAndHour(day, start, end, withDate = false) {
    return `${dateToWeekday(day)} ${withDate ? `${moment(day).format('DD/MM')} ` : ''}${start} - ${end}`;
}

export function weekDayAndDate(day, withDate = true) {
    return `${dateToWeekday(day)} ${withDate ? `${moment(day).format('DD/MM')}` : ''}`;
}
export function toBrowserTimezone(date) {
    if (date) {
        return _momentDateHourToDisplayString(moment(date + ' Z'));
    } else {
        return date;
    }
}

export function isUnlimited(date) {
    return date === UNLIMITED_DATE || toDisplayString(date) === '01/01/3000';
}
export function isZero(date) {
    return date === 0 || toDisplayString(date) === '01/01/1970';
}
// TIMEZONES
export function timezoneToLabel(tzn) {
    if (TIMEZONES[tzn]) {
        const offset = momenttz.tz(tzn).utcOffset();
        return `(GMT${offset ? ` ${momenttz.tz(tzn).format('Z')}` : ''}) ${TIMEZONES[tzn]}`;
    } else {
        return '';
    }
}

export const TIMEZONE_OPTIONS = toSelectionFormat(
    map(
        filter(momenttz.tz.names(), n => Object.keys(TIMEZONES).includes(n)),
        tzn => {
            const offset = momenttz.tz(tzn).utcOffset();
            return {
                id: tzn,
                offset: offset,
                name: timezoneToLabel(tzn)
            };
        }
    ),
    'offset'
);

// PRIVATE
const _momentDateToLongString = momentDate => momentDate.format('D MMMM YYYY');
const _momentDateToString = momentDate => momentDate.format('YYYY-MM-DD');
const _momentDateToDisplayString = momentDate => momentDate.format('DD/MM/YYYY');
const _momentDateHourToDisplayString = momentDate => momentDate.format('DD/MM/YYYY HH:mm');
const _momentDateHourToString = momentDate => momentDate.format('YYYY-MM-DD HH:mm');
const _momentHourToString = momentDate => momentDate.format('HH:mm');
const _momentDateToJavascript = momentDate => ({
    fullDate: _momentDateToString(momentDate),
    weekDay: momentDate.format('ddd'),
    dayAndMonth: momentDate.format('D MMM')
});

export function isAfter(time, otherTime) {
    return moment(time).isAfter(moment(otherTime));
}
